import { Component, computed, effect, inject, input, signal, untracked, viewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { TranslateService } from '@ngx-translate/core';
import { MenuService } from '../../menu.service';
import { FiltersService } from '../../../services/filters.service';
import { FiltersComponent } from '../filters/filters.component';
import { SettingService } from '../../../services/setting.service';
import { SettingComponent } from '../setting/setting.component';
import { applyItemFilters, filterSearchGroups, filterSections, flattenSearchResults, getAllItems, getSearchGroups, hasEnabledItems, toSuperSectionVM } from '../../menu.utils';
import { DataService } from '../../../services/data.service';
import { ItemStatus, MenuDisplayMode } from '../../../shared/enums';
import { ToastService } from '../../../services/toast.service';
import { ModifierComponent } from '../../../shared/modifier/modifier.component';
import { stripQueryParam } from '../../../shared/query-string.utils';
import { ComboPendingMessage } from '../../../services/combo-validation.service';
import { ComboRegistryService } from '../../../services/combo-registry.service';
import { BannerComponent } from '../banner/banner.component';

@Component({
  selector: 'app-menu-list',
  standalone: false,
  templateUrl: './menu-list.component.html',
  styleUrl: './menu-list.component.scss',
})
export class MenuListComponent {
  private menuService = inject(MenuService);
  private router = inject(Router);
  private toastService = inject(ToastService);
  private translateService = inject(TranslateService);
  private bottomSheet = inject(MatBottomSheet);
  private filtersService = inject(FiltersService);
  private settingService = inject(SettingService);
  private dataService = inject(DataService);
  private comboRegistry = inject(ComboRegistryService);

  superSectionId = input.required<string>();
  itemId = input<string | undefined>(undefined, { alias: 'item_id' });
  promotionId = input<string | undefined>(undefined, { alias: 'promotion_id' });
  private openedDeepLinkItem = signal(false);
  // Only a direct external-link entry must carry a promotion_id still listed in config's
  // external_promotions. In-app visits (menu-home, cart back, menuHomeGuard's single-section
  // bounce) don't have one, so they skip the check.
  private isExternalLinkEntry = this.dataService.consumeExternalLinkEntry();
  private isPromotionInvalid = computed(() => {
    if (!this.isExternalLinkEntry) return false;
    const id = this.promotionId();
    return !id || !(this.dataService.externalPromos() ?? []).some(promo => promo.id === id);
  });
  currentSuperSection = computed(() => {
    const section = this.menuService.getSuperSection(this.superSectionId());
    return section && hasEnabledItems(section) ? section : null;
  });

  // No wallpaper → the page's background color shows through.
  wallpaperUrl = computed(() => this.dataService.menuBackgroundUrl() || null);
  hasFilters = computed(() => this.filtersService.hasFilters());
  hasActiveFilters = computed(() => this.filtersService.hasActiveFilters());
  private activeFilterIds = computed(
    () => this.filtersService.activeFilters().map(filter => filter.id),
    { equal: (a, b) => a.join() === b.join() },
  );
  private filteredSuperSection = computed(() => {
    const section = this.currentSuperSection();
    return section ? applyItemFilters(section, this.activeFilterIds()) : null;
  });
  directItems = computed(() => this.filteredSuperSection()?.items ?? []);
  superSectionDescription = computed(() => this.currentSuperSection()?.description?.trim() || null);
  menuDescription = computed(() => this.menuService.menu()?.description?.trim() || null);
  isFilteredEmpty = computed(() => {
    const section = this.filteredSuperSection();
    return this.hasActiveFilters() && !!section && !hasEnabledItems(section);
  });
  hasMultipleLanguages = computed(() => this.settingService.hasMultiLanguage());
  hasMultipleCurrencies = computed(() => this.settingService.hasMultiCurrency());
  menuDisplayMode = computed(() => this.dataService.restaurant()?.menu_display_mode ?? MenuDisplayMode.List);
  isUserSelectDisplayMode = computed(() => this.menuDisplayMode() === MenuDisplayMode.UserSelect);
  visibleSubSections = computed(
    () => (this.filteredSuperSection()?.sub_sections ?? []).filter(hasEnabledItems)
  );
  showSectionScroll = computed(() => this.visibleSubSections().length > 1);

  isSearchOpen = signal(false);
  searchQuery = signal('');
  private searchInputEl = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  private searchableItems = computed(() => {
    const section = this.currentSuperSection();
    return section ? getAllItems([section]) : [];
  });

  private searchGroups = computed(() => {
    const section = this.filteredSuperSection();
    return section ? getSearchGroups([section]) : [];
  });

  // Flat list, no headings - each result carries its own subSectionId (for correct combo
  // tagging) and only shows a section badge when this exact item is ambiguous (also found
  // elsewhere), rather than grouping every result under a heading.
  searchResults = computed(() => flattenSearchResults(filterSearchGroups(this.searchGroups(), this.searchQuery()), this.searchQuery()));

  // When this super-section has no local matches, point the guest at other super-sections
  // that do - same matching rules as the local search, just run over the whole menu.
  matchingSuperSections = computed(() => {
    const query = this.searchQuery();
    if (!query || this.searchResults().length > 0) return [];

    const filterIds = this.activeFilterIds();
    const sections = (this.menuService.menu()?.sections ?? []).map(section => applyItemFilters(section, filterIds));
    return filterSections(sections, query)
      .filter(section => section.id !== this.superSectionId())
      .map(toSuperSectionVM);
  });

  isGridLayout = this.dataService.isGridLayout;
  isGridView = this.menuService.isGridView;
  selectedSubSectionId = signal<string | null>(null);

  titleHeaderHeight = signal(0);
  barHeight = signal(0);
  headerOcclusion = computed(() => this.titleHeaderHeight() + this.barHeight());
  // True once scrolling has pushed the sticky bar up against the title header.
  private isBarAttached = signal(false);
  // Also covers a dead banner image, since bannerUrl() skips failed URLs.
  private hasBanner = computed(() => !!this.bannerCmp()?.bannerUrl());
  // Without a banner the bar already sits flush under the title header, so it takes the styling from the start.
  // An empty bar (no cart/guide/sub-sections) still "attaches" at 0px tall — keep the styling on the title header then.
  isBarStuck = computed(() => (this.isBarAttached() || !this.hasBanner()) && this.barHeight() > 0);

  private bannerCmp = viewChild(BannerComponent);
  private titleHeaderEl = viewChild<ElementRef<HTMLDivElement>>('titleHeader');
  private barEl = viewChild<ElementRef<HTMLDivElement>>('bar');
  private sectionScroll = viewChild<ElementRef<HTMLUListElement>>('sectionScroll');

  private isAutoScrolling = false;
  private scrollRafPending = false;

  constructor() {
    effect(() => {
      if (this.isSearchOpen()) {
        this.searchInputEl()?.nativeElement.focus();
      }
    });

    effect(() => {
      const mode = this.menuDisplayMode();
      if (mode !== MenuDisplayMode.UserSelect) {
        this.isGridView.set(mode === MenuDisplayMode.Grid);
      }
    });

    effect(() => {
      stripQueryParam('promotion_id');
      if (this.isPromotionInvalid()) {
        this.toastService.info(this.translateService.instant('COMMON.PROMOTION_NOT_AVAILABLE'));
        this.router.navigate(['/menu-home']);
        return;
      }

      if (!this.currentSuperSection()) {
        this.toastService.warning(this.translateService.instant('COMMON.SECTION_NOT_FOUND'));
        this.router.navigate(['/menu-home']);
      }
    });

    effect(() => {
      const id = this.itemId();
      const section = this.currentSuperSection();
      if (!id || !section || this.isPromotionInvalid() || this.openedDeepLinkItem()) return;

      this.openedDeepLinkItem.set(true);
      stripQueryParam('item_id');

      const item = this.searchableItems().find(i => i.id === id);
      if (!item) {
        this.toastService.warning(this.translateService.instant('COMMON.ITEM_NOT_FOUND'));
        return;
      }

      if (item.status === ItemStatus.Unavailable) {
        this.toastService.warning(this.translateService.instant('COMMON.ITEM_UNAVAILABLE'));
        return;
      }

      this.bottomSheet.open(ModifierComponent, { autoFocus: false, data: { item } });
    });

    effect(() => {
      const sections = this.visibleSubSections();
      const selectedId = this.selectedSubSectionId();
      if (!this.searchQuery() && sections.length > 0 && !sections.some(section => section.id === selectedId)) {
        this.selectedSubSectionId.set(sections[0].id);
      }
    });

    let isFirstFilterRun = true;
    effect(() => {
      this.activeFilterIds();
      if (isFirstFilterRun) {
        isFirstFilterRun = false;
        return;
      }
      untracked(() => window.scrollTo({ top: 0 }));
    });

    effect(() => {
      if (this.searchQuery()) {
        this.selectedSubSectionId.set(null);
      } else {
        this.syncSelectedSubSectionToScroll();
      }
    });

    effect((onCleanup) => {
      const el = this.titleHeaderEl()?.nativeElement;
      if (!el) return;

      const observer = new ResizeObserver(() => this.titleHeaderHeight.set(el.offsetHeight));
      observer.observe(el);
      this.titleHeaderHeight.set(el.offsetHeight);
      onCleanup(() => observer.disconnect());
    });

    effect((onCleanup) => {
      const el = this.barEl()?.nativeElement;
      if (!el) return;

      const observer = new ResizeObserver(() => this.barHeight.set(el.offsetHeight));
      observer.observe(el);
      this.barHeight.set(el.offsetHeight);
      onCleanup(() => observer.disconnect());
    });
  }

  toggleSearch(): void {
    if (this.isSearchOpen()) {
      this.closeSearch();
    } else {
      this.isSearchOpen.set(true);
    }
  }

  closeSearch(): void {
    this.isSearchOpen.set(false);
    this.searchQuery.set('');
  }

  openFilters(): void {
    this.bottomSheet.open(FiltersComponent);
  }

  clearFilters(): void {
    this.filtersService.clearFilters();
  }

  openSettings(): void {
    this.bottomSheet.open(SettingComponent);
  }

  // Clicking a combo guide message scrolls to it when it belongs to the section already
  // on screen; a message about a different super-section has nothing here to scroll
  // to, so it just tells the guest where to find it instead.
  onComboGuideMessageClick(message: ComboPendingMessage): void {
    if (message.groupId === this.currentSuperSection()?.id) {
      this.selectSubSection(message.subId);
      return;
    }

    const superSectionName = this.comboRegistry.getGroups().find(group => group.groupId === message.groupId)?.name ?? '';
    this.toastService.info(this.translateService.instant('COMBO.ON_ANOTHER_SECTION', { section: superSectionName }));
  }

  selectSubSection(sectionId: string): void {
    this.scrollFilterIntoView(sectionId);

    if (this.selectedSubSectionId() === sectionId) {
      this.syncSelectedSubSectionToScroll();
      return;
    }

    this.selectedSubSectionId.set(sectionId);
    this.scrollToSubSection(sectionId);
  }

  private scrollToSubSection(sectionId: string): void {
    const target = document.getElementById(`sub-section-${sectionId}`);
    if (!target) return;

    const occlusion = this.headerOcclusion();
    const targetOffset = target.getBoundingClientRect().top + window.scrollY;
    const top = Math.max(targetOffset - occlusion - 8, 0);

    this.isAutoScrolling = true;
    window.scrollTo({ top, behavior: 'smooth' });
    this.waitForScrollEnd();
  }

  private waitForScrollEnd(): void {
    const done = () => {
      this.isAutoScrolling = false;
      document.removeEventListener('scrollend', done);
    };
    if ('onscrollend' in window) {
      document.addEventListener('scrollend', done, { once: true });
    } else {
      setTimeout(done, 500);
    }
  }

  onPageScroll(): void {
    if (this.scrollRafPending) return;
    this.scrollRafPending = true;
    requestAnimationFrame(() => {
      this.scrollRafPending = false;
      this.syncBarAttached();
      if (this.isAutoScrolling) return;
      this.syncSelectedSubSectionToScroll();
    });
  }

  private syncBarAttached(): void {
    const bar = this.barEl()?.nativeElement;
    if (!bar) return;
    // Bar's sticky top is titleHeaderHeight - 1; +1 tolerance for sub-pixel rounding.
    this.isBarAttached.set(window.scrollY > 0 && bar.getBoundingClientRect().top <= this.titleHeaderHeight());
  }

  private syncSelectedSubSectionToScroll(): void {
    if (this.isAutoScrolling) return;

    const occlusion = this.headerOcclusion();
    const activationLine = occlusion + window.innerHeight * 0.25;

    const sections = this.visibleSubSections();
    let activeId: string | null = sections[0]?.id ?? null;
    for (const section of sections) {
      const target = document.getElementById(`sub-section-${section.id}`);
      if (!target) continue;
      const top = target.getBoundingClientRect().top;
      if (top - activationLine <= 8) {
        activeId = section.id;
      } else {
        break;
      }
    }

    if (activeId !== this.selectedSubSectionId()) {
      this.selectedSubSectionId.set(activeId);
      this.scrollFilterIntoView(activeId);
    }
  }

  private scrollFilterIntoView(sectionId: string | null): void {
    if (!sectionId) return;
    const scrollContainer = this.sectionScroll()?.nativeElement;
    const target = scrollContainer?.querySelector<HTMLElement>(`[data-section-id="${sectionId}"]`);
    if (!scrollContainer || !target) return;

    const edgePadding = 16;
    const containerRect = scrollContainer.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();

    let delta = 0;
    if (targetRect.left < containerRect.left + edgePadding) {
      delta = targetRect.left - (containerRect.left + edgePadding);
    } else if (targetRect.right > containerRect.right - edgePadding) {
      delta = targetRect.right - (containerRect.right - edgePadding);
    }
    if (delta === 0) return;

    scrollContainer.scrollTo({ left: scrollContainer.scrollLeft + delta, behavior: 'smooth' });
  }
}